import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Archive, Trash2, RotateCcw, Search, Loader2, AlertTriangle, 
  Clock, ChevronDown, ChevronUp, Calendar, Building2
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import permissionsService from '../../services/permissionsService';
import { Avatar } from '../ui';

const ArchivedAssessments = ({ 
  isExpanded = false, 
  onToggle,
  onRefreshLibrary,
  asSection = true,
  refreshTrigger = 0
}) => {
  const { user } = useContext(AuthContext);
  const [archivedAssessments, setArchivedAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [permanentDeleting, setPermanentDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);

  const fetchArchivedAssessments = async () => {
    setLoading(true);
    try {
      const response = await AssessmentBuilderAPI.getArchivedAssessments({ search: searchTerm });
      setArchivedAssessments(response.results || []);
    } catch (error) {
      console.error('Error fetching archived assessments:', error);
      toast.error('Failed to load archived assessments');
    } finally {
      setLoading(false);
    }
  };

  // Check permissions and fetch count on mount
  useEffect(() => {
    const initializeSection = async () => {
      try {
        // Check delete permission
        const canDelete = await permissionsService.hasPermission(
          permissionsService.PERMISSIONS.DELETE_ASSESSMENT
        );
        setHasDeletePermission(canDelete || user?.is_superuser);
        
        // Fetch count to know if we should show the section
        const response = await AssessmentBuilderAPI.getArchivedAssessments({ limit: 1 });
        setArchivedCount(response.count || response.results?.length || 0);
      } catch (error) {
        console.error('Error initializing archived section:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    
    initializeSection();
  }, [user]);

  useEffect(() => {
    if (isExpanded || !asSection) {
      fetchArchivedAssessments();
    }
  }, [isExpanded, asSection]);

  // Refresh when parent triggers it (e.g., after delete)
  useEffect(() => {
    if (refreshTrigger > 0) {
      // Always update count when triggered
      AssessmentBuilderAPI.getArchivedAssessments({ limit: 1 })
        .then(response => setArchivedCount(response.count || response.results?.length || 0))
        .catch(console.error);
      
      // Fetch full list if expanded
      if (isExpanded || !asSection) {
        fetchArchivedAssessments();
      }
    }
  }, [refreshTrigger, isExpanded, asSection]);

  useEffect(() => {
    if (isExpanded || !asSection) {
      const debounce = setTimeout(() => {
        fetchArchivedAssessments();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm]);

  const handleRestore = async (assessment) => {
    setRestoring(assessment.id);
    try {
      await AssessmentBuilderAPI.restoreAssessment(assessment.uuid);
      toast.success(`"${assessment.name}" has been restored`);
      fetchArchivedAssessments();
      if (onRefreshLibrary) onRefreshLibrary();
    } catch (error) {
      console.error('Error restoring assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to restore assessment');
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async (assessment) => {
    setPermanentDeleting(assessment.id);
    try {
      await AssessmentBuilderAPI.permanentDeleteAssessment(assessment.uuid);
      toast.success(`"${assessment.name}" has been permanently deleted`);
      setConfirmDelete(null);
      fetchArchivedAssessments();
    } catch (error) {
      console.error('Error permanently deleting assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to permanently delete assessment');
    } finally {
      setPermanentDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Chip tone for the time left before permanent deletion.
  const getDaysRemainingColor = (days) => {
    if (days <= 7) return 'badge-danger';
    if (days <= 30) return 'badge-warning';
    return 'badge-gray';
  };


  const content = (
    <div>
      {/* Search */}
      <div className="px-5 pb-4 pt-1">
        <div className="relative sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search archived assessments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input h-9 pl-9"
          />
        </div>

        {/* Info Banner */}
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-warning-500/30 bg-warning-50 px-4 py-3 dark:bg-warning-500/10">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="text-[12.5px] leading-relaxed text-warning-800 dark:text-warning-400">
            Archived assessments will be permanently deleted after 90 days. 
            Restore them to prevent automatic deletion.
          </p>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center border-t border-gray-100 py-10 dark:border-white/[0.06]">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      )}

      {/* Empty State */}
      {!loading && archivedAssessments.length === 0 && (
        <div className="border-t border-gray-100 px-6 py-10 text-center dark:border-white/[0.06]">
          <Archive className="mx-auto mb-3 h-6 w-6 text-gray-400 dark:text-gray-500" />
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">No archived assessments</p>
          <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
            Deleted assessments will appear here for 90 days
          </p>
        </div>
      )}

      {/* Archived List */}
      {!loading && archivedAssessments.length > 0 && (
        <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
          {archivedAssessments.map((assessment) => (
            <div
              key={assessment.id}
              className="flex flex-col gap-3 px-5 py-3.5 transition-colors hover:bg-gray-50/80 sm:flex-row sm:items-center sm:justify-between dark:hover:bg-white/[0.03]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="truncate text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{assessment.name}</h4>
                  {/* Days Remaining Badge */}
                  <span className={`badge ${getDaysRemainingColor(assessment.days_remaining)}`}>
                    <Clock className="h-3 w-3" />
                    {assessment.days_remaining} days left
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px] text-gray-500 dark:text-gray-400">
                  <span className="badge badge-gray">
                    <Building2 className="h-3 w-3" />
                    {assessment.library_type_display}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    Deleted {formatDate(assessment.deleted_at)}
                  </span>
                  {assessment.deleted_by && (
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar name={assessment.deleted_by} size="xs" />
                      {assessment.deleted_by}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  onClick={() => handleRestore(assessment)}
                  disabled={restoring === assessment.id}
                  className="btn btn-secondary btn-sm"
                >
                  {restoring === assessment.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                  Restore
                </button>
                {user?.is_superuser && (
                  <button
                    onClick={() => setConfirmDelete(assessment)}
                    disabled={permanentDeleting === assessment.id}
                    className="btn btn-danger-soft btn-sm"
                  >
                    {permanentDeleting === assessment.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Delete Now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-950/40 dark:bg-black/70 flex items-center justify-center z-[70] p-4"
            onClick={() => !permanentDeleting && setConfirmDelete(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="surface-overlay w-full max-w-md overflow-hidden"
            >
              <div className="flex items-start gap-3 px-6 py-5">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-danger-500 dark:text-danger-400" />
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Permanently Delete Assessment?</h3>
                  <p className="mt-1 text-[13.5px] text-gray-600 dark:text-gray-300">
                    This will permanently delete <strong className="font-semibold text-gray-900 dark:text-gray-50">"{confirmDelete.name}"</strong>. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
                <button
                  onClick={() => setConfirmDelete(null)}
                  disabled={permanentDeleting}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePermanentDelete(confirmDelete)}
                  disabled={permanentDeleting}
                  className="btn btn-danger"
                >
                  {permanentDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Delete Permanently
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Hide section if still loading initial state
  if (initialLoading && asSection) {
    return null;
  }

  // Hide section if no archived content AND user doesn't have delete permission
  if (asSection && archivedCount === 0 && !hasDeletePermission) {
    return null;
  }

  // If used as a standalone page, return content directly
  if (!asSection) {
    return (
      <div>
        <div className="mb-6 flex items-center gap-3">
          <div>
            <h1 className="page-title">Archived Assessments</h1>
            <p className="page-subtitle">
              Items will be permanently deleted after 90 days
            </p>
          </div>
        </div>
        <div className="card overflow-hidden pt-4">
          {content}
        </div>
      </div>
    );
  }

  // As a collapsible section: one card, header row inside
  return (
    <div className="card overflow-hidden">
      <div 
        className="group flex cursor-pointer flex-wrap items-center justify-between gap-3 px-5 py-4"
        onClick={onToggle}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Archived</h3>
              {archivedAssessments.length > 0 && (
                <span className="badge badge-gray tabular-nums">
                  {archivedAssessments.length}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">Deleted assessments (90-day retention)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300" /> : <ChevronDown className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300" />}
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArchivedAssessments;
